import {
  Button,
  FlatTree,
  FlatTreeItem,
  InputOnChangeData,
  Label,
  makeStyles,
  SearchBox,
  SearchBoxChangeEvent,
  Tooltip,
  TreeCheckedChangeData,
  TreeCheckedChangeEvent,
  TreeItemLayout,
  TreeOpenChangeData,
  TreeOpenChangeEvent,
} from "@fluentui/react-components";
import { FlatVsaaTag } from "../../models/VsaaTag";
import { useCallback, useMemo, useState } from "react";
import { VsaaTagGroup } from "./VsaaTagGroup";
import {
  distinct,
  getAllAncestors,
  getAncestors,
  getTag,
  searchTags,
} from "../../services/TagService";
import {
  ArrowExpandAll20Regular,
  ArrowCollapseAll20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  searchInput: {
    marginBottom: "20px",
    width: "300px",
  },
  tree: {
    marginBottom: "20px",
  },
  tagContainer: {
    display: "flex",
    flexDirection: "column",
  },
  treeHeader: {
    display: "flex",
    justifyContent: "space-between",
  },
  treeHeaderButton: {
    height: "33px",
    marginLeft: "8px",
  },
  treeItemText: {
    display: "inline-flex",
    gap: "10px",
  },
  treeItemInUse: {
    display: "inline-flex",
    alignItems: "center",
    textAlign: "center",
  },
});

interface TreeTagPickerProps {
  multiValue?: boolean;
  values: FlatVsaaTag[];
  tags: FlatVsaaTag[];
  onChange: (selectedTags: FlatVsaaTag[]) => void;
  rearrange?: boolean;
  usedTagsId?: string[];
  usedTagLabel?: string;
  processWithSubActivitiesId?: string[];
  disableRootNodes?: boolean;
}

export const TreeTagPicker = (props: TreeTagPickerProps) => {
  const classes = useStyles();
  const {
    values,
    tags,
    multiValue,
    onChange,
    usedTagsId,
    usedTagLabel,
    processWithSubActivitiesId,
    disableRootNodes,
  } = props;
  const [query, setQuery] = useState<string>("");
  const [searchHitIds, setSearchHitIds] = useState<string[]>([]);

  const [openItems, setOpenItems] = useState<string[]>(() => {
    const allTagsToOpen = getAllAncestors(values, tags);
    return allTagsToOpen.map((t) => t.id);
  });

  const currentTags = useMemo<FlatVsaaTag[]>(() => {
    if (searchHitIds.length === 0) {
      const visibleTags = tags.filter((t) => {
        if (t.level && t.level < 3) {
          return true;
        }

        if (t.parentId && openItems.includes(t.parentId!)) {
          return true;
        }

        return false;
      });

      return visibleTags;
    } else {
      const searchTags = tags.filter(
        (t) =>
          t.level === 1 || (t.parentId && searchHitIds.indexOf(t.id) !== -1)
      );

      return searchTags;
    }
  }, [tags, openItems, searchHitIds]);

  const { t } = useTranslation();

  const valueIds = useMemo<string[]>(() => {
    return values.map((t) => t.id);
  }, [values]);

  const tagIsOpen = useCallback(
    (tagId: string) => openItems.indexOf(tagId) !== -1,
    [openItems]
  );

  const onTagsChange = (tagIds: string[]) => {
    const selectedTagIds = new Set(tagIds);
    const newTags = tags.filter((tag) => selectedTagIds.has(tag.id));
    onChange(newTags);
  };

  const handleOpenChange = (
    _event: TreeOpenChangeEvent,
    data: TreeOpenChangeData
  ) => {
    let newOpenItems: string[];
    const tagId = data.value as string;

    if (data.open && !tagIsOpen(tagId)) {
      newOpenItems = [...openItems, tagId];
      setOpenItems(newOpenItems);
    } else if (!data.open && tagIsOpen(tagId)) {
      newOpenItems = openItems.filter((id) => id !== tagId);
      setOpenItems(newOpenItems);
    }
  };

  const expandAll = () => {
    const selectedChildren = tags.filter(
      (t) => t.parentId && valueIds.indexOf(t.parentId) !== -1
    );
    const newOpenItems = [
      ...openItems,
      ...valueIds,
      ...selectedChildren.map((t) => t.id),
    ];

    setOpenItems(newOpenItems.filter(distinct));
  };

  const closeAll = () => {
    if (props.rearrange) {
      setOpenItems([]);
    } else {
      setOpenItems(
        openItems.filter((id) => {
          const tag = getTag(id, tags);
          return tag.level && tag.level <= 1;
        })
      );
    }
  };

  const onCheckedChange = (
    _event: TreeCheckedChangeEvent,
    data: TreeCheckedChangeData
  ) => {
    const tagId = data.value.toString();
    const tag = getTag(tagId, tags);
    if (!props.rearrange && !tag.parentId) {
      return;
    }
    if (disableRootNodes && tag.level === 2) {
      return;
    }
    let newSelectedItemIds: string[];

    if (data.checked) {
      if (valueIds.includes(tagId)) return;
      newSelectedItemIds = multiValue ? [...valueIds, tagId] : [tagId];
    } else {
      newSelectedItemIds = valueIds.filter((t) => t !== tagId);
    }
    if (usedTagsId && usedTagsId.includes(tag.id)) {
      _event.preventDefault();
      _event.stopPropagation();
      return;
    }
    // Only prevent unchecking (removing) tags that have subactivities, not adding new ones
    if (
      !data.checked &&
      processWithSubActivitiesId &&
      processWithSubActivitiesId.includes(tag.id)
    ) {
      _event.preventDefault();
      _event.stopPropagation();
      return;
    }
    onTagsChange(newSelectedItemIds);
  };

  const onTagDismiss = (tag: FlatVsaaTag) => {
    if (processWithSubActivitiesId?.find((f) => f === tag.id)) {
      return;
    }
    const newSelectedItemIds = valueIds.filter((id) => id !== tag.id);
    onTagsChange(newSelectedItemIds);
  };

  const onSearchChange = (
    ev: SearchBoxChangeEvent,
    data: InputOnChangeData
  ) => {
    ev.preventDefault();

    setQuery(data.value);
  };

  const handleSearch = () => {
    if (query.length < 3) {
      return;
    }

    if (!query) {
      setSearchHitIds([]);
      setOpenItems([]);
      return;
    }

    const foundTags = searchTags(query, tags);

    let tagsToOpen: string[] = [];
    let allSearchHitIDs: string[] = [];

    foundTags.forEach((tag) => {
      if (!tag.ancestorIds) {
        return;
      }

      tagsToOpen.push(...tag.ancestorIds);
      allSearchHitIDs.push(...tag.ancestorIds, tag.id);
    });

    tagsToOpen = tagsToOpen.filter(distinct);
    allSearchHitIDs = allSearchHitIDs.filter(distinct);

    setSearchHitIds(allSearchHitIDs);
    setOpenItems(tagsToOpen);
  };

  const getTreeNodes = () => {
    return currentTags.map(getTreeNode);
  };

  const getTreeNode = (tag: FlatVsaaTag) => {
    if (tag.level === 1 && !props.rearrange) {
      return null;
    }

    // get all ancestors
    try {
      const ancestors = getAncestors(tag, [], currentTags);

      //if not rearange only show level 2 and above
      const visibleAncestors = props.rearrange
        ? ancestors
        : ancestors.filter((a) => (a.level ?? 0) > 1);

      if (!visibleAncestors.every((a) => tagIsOpen(a.id))) {
        return null;
      }

      //make level 2 the root if not rearrange
      const parentValue =
        !props.rearrange && tag.level === 2 ? undefined : tag.parentId;

      const isDisabledDueToSubActivities =
        valueIds.includes(tag.id) &&
        processWithSubActivitiesId?.includes(tag.id);
      const isDisabledDueToUsage = usedTagsId?.includes(tag.id);
      const isDisabledDueToRootNode = disableRootNodes && tag.level === 2;
      const isDisabled =
        isDisabledDueToUsage ||
        isDisabledDueToSubActivities ||
        isDisabledDueToRootNode;

      return (
        <FlatTreeItem
          key={tag.id}
          parentValue={parentValue}
          value={tag.id}
          aria-level={
            !props.rearrange && tag.level ? tag.level - 1 : tag.level || 1
          }
          aria-setsize={tag.siblings ?? 1}
          aria-posinset={tag.position ?? 1}
          aria-hidden={false}
          tabIndex={0}
          open={tagIsOpen(tag.id)}
          itemType={
            tag.hasChildren && searchHitIds.length === 0 ? "branch" : "leaf"
          }
          data-disabled={isDisabled ? "true" : undefined}
        >
          <TreeItemLayout>
            {(() => {
              const content = (
                <span
                  className={classes.treeItemText}
                  style={
                    isDisabled
                      ? {
                          opacity: 0.5,
                          pointerEvents: "none",
                          cursor: "not-allowed",
                        }
                      : undefined
                  }
                >
                  {tag.title}{" "}
                  {usedTagsId?.includes(tag.id) && (
                    <span className={classes.treeItemInUse}>
                      <Warning20Regular />
                      {usedTagLabel ?? t("processInUse")}
                    </span>
                  )}
                  {isDisabledDueToSubActivities && (
                    <span className={classes.treeItemInUse}>
                      <Warning20Regular />
                      {t("processHasSubActivities")}
                    </span>
                  )}
                </span>
              );
              return tag.path && tag.path.includes("/") ? (
                <Tooltip content={tag.path} relationship="label">
                  {content}
                </Tooltip>
              ) : (
                content
              );
            })()}
          </TreeItemLayout>
        </FlatTreeItem>
      );
    } catch {
      return null;
    }
  };

  return (
    <>
      <div className={classes.treeHeader}>
        <SearchBox
          className={classes.searchInput}
          placeholder={t("searchTag")}
          value={query}
          onChange={onSearchChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        />
        <div>
          <Button
            className={classes.treeHeaderButton}
            icon={<ArrowExpandAll20Regular />}
            onClick={expandAll}
            disabled={searchHitIds.length > 0}
          >
            {t("openSection")}
          </Button>
          <Button
            className={classes.treeHeaderButton}
            icon={<ArrowCollapseAll20Regular />}
            onClick={closeAll}
            disabled={searchHitIds.length > 0}
          >
            {t("closeAll")}
          </Button>
        </div>
      </div>

      <FlatTree
        className={classes.tree}
        openItems={openItems}
        onOpenChange={handleOpenChange}
        aria-label={t("treeView")}
        selectionMode={multiValue ? "multiselect" : "single"}
        checkedItems={valueIds}
        onCheckedChange={onCheckedChange}
      >
        {getTreeNodes()}
      </FlatTree>
      <div className={classes.tagContainer}>
        <Label weight="semibold">
          {multiValue ? t("selectedTags") : t("selectedTag")}
        </Label>
        <VsaaTagGroup
          tags={valueIds.map((id) => getTag(id, tags))}
          onDismiss={onTagDismiss}
        />
      </div>
    </>
  );
};
